<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { firstAccessiblePath, safeNextPath } from '@/app/access'
import { ApiError } from '@/shared/api/http'
import LanguageSwitch from '@/shared/components/LanguageSwitch.vue'
import { useAuthStore } from './store'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const loading = ref(false)
const error = ref('')
const form = reactive({ username: '', password: '', remember: false })
const { t } = useI18n()

async function submit() {
  if (loading.value) return
  if (!form.username.trim() || !form.password) {
    error.value = t('auth.empty')
    return
  }
  loading.value = true
  error.value = ''
  try {
    await auth.login(form.username.trim(), form.password, form.remember)
    const next = safeNextPath(route.query.next, firstAccessiblePath(auth.can))
    await router.replace(next)
  } catch (cause) {
    error.value = cause instanceof ApiError ? cause.message : t('auth.loginFailed')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="login-split">
    <!-- ============ 左：配图区 ============ -->
    <aside class="login-art-panel">
      <div class="login-art-brand">
        <svg viewBox="0 0 40 40" aria-hidden="true">
          <defs>
            <linearGradient id="cwArtG" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stop-color="#3F7BFF" /><stop offset="1" stop-color="#0FA6C2" />
            </linearGradient>
          </defs>
          <rect width="40" height="40" rx="11" fill="url(#cwArtG)" />
          <g transform="translate(20,20)" stroke="#fff" stroke-width="2.6" stroke-linecap="round">
            <line x1="0" y1="-9.5" x2="0" y2="9.5" />
            <line x1="-8.2" y1="-4.75" x2="8.2" y2="4.75" />
            <line x1="-8.2" y1="4.75" x2="8.2" y2="-4.75" />
          </g>
        </svg>
        <span>{{ t('auth.brand') }} ColdWise</span>
      </div>

      <div class="login-art">
        <svg viewBox="0 0 520 520" fill="none" xmlns="http://www.w3.org/2000/svg" role="img"
          :aria-label="t('auth.artLabel')">
          <defs>
            <filter id="cwArtShadow" x="-40%" y="-30%" width="180%" height="180%">
              <feDropShadow dx="0" dy="7" stdDeviation="11" flood-color="#165DFF" flood-opacity="0.13" />
            </filter>
          </defs>

          <!-- 背景弧线 -->
          <path d="M-30 300 C 120 218, 340 380, 550 268" stroke="#E2EEFB" stroke-width="1.8" />
          <path d="M-30 364 C 112 282, 332 442, 550 332" stroke="#CFE1FA" stroke-width="2" />
          <path d="M-30 424 C 132 352, 360 502, 550 402" stroke="#D8EFEE" stroke-width="2" />

          <!-- 冷库货架 -->
          <rect x="88" y="136" width="9" height="300" rx="4.5" fill="#B9D4F5" />
          <rect x="323" y="136" width="9" height="300" rx="4.5" fill="#B9D4F5" />
          <rect x="84" y="136" width="252" height="9" rx="4.5" fill="#9CC2F0" />
          <rect x="84" y="231" width="252" height="9" rx="4.5" fill="#9CC2F0" />
          <rect x="84" y="326" width="252" height="9" rx="4.5" fill="#9CC2F0" />
          <rect x="84" y="421" width="252" height="9" rx="4.5" fill="#9CC2F0" />

          <!-- 层 1 -->
          <g>
            <rect x="100" y="160" width="62" height="66" rx="6" fill="#165DFF" />
            <rect x="100" y="160" width="62" height="13" rx="6" fill="#3F7BFF" />
            <line x1="131" y1="173" x2="131" y2="226" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>
          <g>
            <rect x="170" y="160" width="62" height="66" rx="6" fill="#0FC6C2" />
            <rect x="170" y="160" width="62" height="13" rx="6" fill="#37D4D0" />
            <line x1="201" y1="173" x2="201" y2="226" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>
          <g>
            <rect x="240" y="160" width="62" height="66" rx="6" fill="#4D8DFF" />
            <rect x="240" y="160" width="62" height="13" rx="6" fill="#6FA4FF" />
            <line x1="271" y1="173" x2="271" y2="226" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>

          <!-- 层 2 -->
          <g>
            <rect x="100" y="255" width="62" height="66" rx="6" fill="#4D8DFF" />
            <rect x="100" y="255" width="62" height="13" rx="6" fill="#6FA4FF" />
            <line x1="131" y1="268" x2="131" y2="321" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>
          <g>
            <rect x="170" y="255" width="62" height="66" rx="6" fill="#165DFF" />
            <rect x="170" y="255" width="62" height="13" rx="6" fill="#3F7BFF" />
            <line x1="201" y1="268" x2="201" y2="321" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>
          <g>
            <rect x="240" y="255" width="62" height="66" rx="6" fill="#0FC6C2" />
            <rect x="240" y="255" width="62" height="13" rx="6" fill="#37D4D0" />
            <line x1="271" y1="268" x2="271" y2="321" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>

          <!-- 层 3 -->
          <g>
            <rect x="100" y="350" width="62" height="66" rx="6" fill="#0FC6C2" />
            <rect x="100" y="350" width="62" height="13" rx="6" fill="#37D4D0" />
            <line x1="131" y1="363" x2="131" y2="416" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>
          <g>
            <rect x="170" y="350" width="62" height="66" rx="6" fill="#9CC2F0" />
            <rect x="170" y="350" width="62" height="13" rx="6" fill="#B4D3F7" />
            <line x1="201" y1="363" x2="201" y2="416" stroke="#fff" stroke-opacity=".5" stroke-width="2.4" />
          </g>

          <!-- 温度计 -->
          <rect x="40" y="176" width="20" height="150" rx="10" fill="#fff" stroke="#B9D4F5" stroke-width="2.5" />
          <rect x="45" y="196" width="10" height="112" rx="5" fill="#165DFF" />
          <circle cx="50" cy="326" r="15" fill="#165DFF" />
          <circle cx="50" cy="326" r="15" stroke="#B9D4F5" stroke-width="2.5" />
          <line x1="64" y1="202" x2="75" y2="202" stroke="#B9D4F5" stroke-width="2.5" stroke-linecap="round" />
          <line x1="64" y1="236" x2="72" y2="236" stroke="#CFE1FA" stroke-width="2.5" stroke-linecap="round" />
          <line x1="64" y1="270" x2="75" y2="270" stroke="#B9D4F5" stroke-width="2.5" stroke-linecap="round" />
          <g transform="translate(16,128)">
            <rect width="78" height="30" rx="8" fill="#fff" stroke="#B9D4F5" stroke-width="2" />
            <text x="39" y="20" text-anchor="middle" font-size="14" font-weight="700" fill="#165DFF">-18°C</text>
          </g>

          <!-- 库存看板 -->
          <g transform="translate(330,84)">
            <rect width="152" height="104" rx="14" fill="#fff" filter="url(#cwArtShadow)" />
            <rect x="16" y="16" width="54" height="8" rx="4" fill="#DCE7F6" />
            <rect x="18" y="60" width="18" height="28" rx="5" fill="#CFE1FA" />
            <rect x="46" y="44" width="18" height="44" rx="5" fill="#9CC2F0" />
            <rect x="74" y="26" width="18" height="62" rx="5" fill="#165DFF" />
            <path d="M104 76 L119 56 L134 64" stroke="#0FC6C2" stroke-width="3" stroke-linecap="round"
              stroke-linejoin="round" />
          </g>

          <!-- 保质期预警 -->
          <g transform="translate(28,392)">
            <rect width="148" height="80" rx="14" fill="#fff" filter="url(#cwArtShadow)" />
            <circle cx="29" cy="40" r="14" fill="#FFF1E6" />
            <path d="M29 32 v9 h6" stroke="#FF7D00" stroke-width="2.6" stroke-linecap="round" />
            <rect x="53" y="28" width="72" height="8" rx="4" fill="#E5EAF2" />
            <rect x="53" y="44" width="46" height="8" rx="4" fill="#FFD9B8" />
          </g>

          <!-- 冷链车 -->
          <g transform="translate(346,398)">
            <rect x="0" y="0" width="112" height="66" rx="10" fill="#E8F3FF" stroke="#B9D4F5" stroke-width="2.5" />
            <path d="M112 18 h28 a9 9 0 0 1 9 9 v39 h-37 z" fill="#9CC2F0" />
            <rect x="121" y="27" width="21" height="16" rx="4" fill="#F2F8FF" />
            <g transform="translate(40,33)" stroke="#165DFF" stroke-width="2.6" stroke-linecap="round">
              <line x1="0" y1="-9" x2="0" y2="9" />
              <line x1="-7.8" y1="-4.5" x2="7.8" y2="4.5" />
              <line x1="-7.8" y1="4.5" x2="7.8" y2="-4.5" />
            </g>
            <circle cx="30" cy="70" r="13" fill="#5C6B7F" />
            <circle cx="30" cy="70" r="5" fill="#DDE5EF" />
            <circle cx="122" cy="70" r="13" fill="#5C6B7F" />
            <circle cx="122" cy="70" r="5" fill="#DDE5EF" />
          </g>

          <!-- 雪花点缀 -->
          <g stroke="#8FC0F5" stroke-width="2.4" stroke-linecap="round">
            <g transform="translate(296,72)">
              <line x1="0" y1="-11" x2="0" y2="11" />
              <line x1="-9.5" y1="-5.5" x2="9.5" y2="5.5" />
              <line x1="-9.5" y1="5.5" x2="9.5" y2="-5.5" />
            </g>
            <g transform="translate(228,104) scale(.72)" stroke="#B9D4F5">
              <line x1="0" y1="-11" x2="0" y2="11" />
              <line x1="-9.5" y1="-5.5" x2="9.5" y2="5.5" />
              <line x1="-9.5" y1="5.5" x2="9.5" y2="-5.5" />
            </g>
            <g transform="translate(452,332) scale(1.15)" stroke="#9AD9D7">
              <line x1="0" y1="-11" x2="0" y2="11" />
              <line x1="-9.5" y1="-5.5" x2="9.5" y2="5.5" />
              <line x1="-9.5" y1="5.5" x2="9.5" y2="-5.5" />
            </g>
            <g transform="translate(76,152) scale(.85)" stroke="#B9D4F5">
              <line x1="0" y1="-11" x2="0" y2="11" />
              <line x1="-9.5" y1="-5.5" x2="9.5" y2="5.5" />
              <line x1="-9.5" y1="5.5" x2="9.5" y2="-5.5" />
            </g>
          </g>
        </svg>
      </div>

      <div class="login-art-tag">{{ t('auth.leftTag') }}</div>
    </aside>

    <!-- ============ 右：登录 ============ -->
    <main class="login-form-panel">
      <div class="login-lang">
        <LanguageSwitch />
      </div>

      <div class="login-panel">
        <div class="login-logo">
          <svg viewBox="0 0 40 40" aria-hidden="true">
            <defs>
              <linearGradient id="cwLogoG" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#3F7BFF" /><stop offset="1" stop-color="#0FA6C2" />
              </linearGradient>
            </defs>
            <rect width="40" height="40" rx="11" fill="url(#cwLogoG)" />
            <g transform="translate(20,20)" stroke="#fff" stroke-width="2.6" stroke-linecap="round">
              <line x1="0" y1="-9.5" x2="0" y2="9.5" />
              <line x1="-8.2" y1="-4.75" x2="8.2" y2="4.75" />
              <line x1="-8.2" y1="4.75" x2="8.2" y2="-4.75" />
            </g>
          </svg>
          <div>
            <div class="login-logo-zh">{{ t('auth.brand') }}</div>
            <div class="login-logo-en">{{ t('auth.brandEn') }}</div>
          </div>
        </div>

        <h1 class="login-title">{{ t('auth.pageTitle') }}</h1>
        <p class="login-sub">{{ t('auth.pageSubtitle') }}</p>

        <a-alert v-if="error" type="error" class="login-error" :show-icon="true">{{ error }}</a-alert>

        <div class="login-field">
          <a-input v-model="form.username" size="large" allow-clear :placeholder="t('auth.usernamePlaceholder')"
            autocomplete="username" @press-enter="submit" @input="error = ''">
            <template #prefix>
              <svg class="login-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </template>
          </a-input>
        </div>

        <div class="login-field">
          <a-input-password v-model="form.password" size="large" :placeholder="t('auth.passwordPlaceholder')"
            autocomplete="current-password" @press-enter="submit" @input="error = ''">
            <template #prefix>
              <svg class="login-field-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </template>
          </a-input-password>
        </div>

        <div class="login-row">
          <a-checkbox v-model="form.remember">{{ t('auth.remember') }}</a-checkbox>
        </div>

        <a-button type="primary" size="large" long :loading="loading" @click="submit">
          {{ loading ? t('auth.signingIn') : t('auth.login') }}
        </a-button>
      </div>

      <div class="login-foot">{{ t('auth.footer') }}</div>
    </main>
  </div>
</template>

<style scoped>
.login-split {
  display: flex;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

/* ---------- 左：配图 ---------- */
.login-art-panel {
  position: relative;
  flex: 0 0 47%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(155deg, #e9f3ff 0%, #f3f9ff 42%, #e9f8f7 100%);
  overflow: hidden;
}

.login-art-panel::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(22, 93, 255, 0.06);
}

.login-art {
  width: min(78%, 560px);
  max-height: 78vh;
}

.login-art svg {
  width: 100%;
  height: auto;
  display: block;
}

.login-art-brand {
  position: absolute;
  top: 26px;
  left: 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #2b4a7e;
  letter-spacing: 0.4px;
}

.login-art-brand svg {
  width: 26px;
  height: 26px;
}

.login-art-tag {
  position: absolute;
  bottom: 30px;
  left: 32px;
  font-size: 12px;
  color: #6b87ae;
  letter-spacing: 0.6px;
}

/* ---------- 右：登录 ---------- */
.login-form-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  position: relative;
  padding: 32px 24px;
}

.login-lang {
  position: absolute;
  top: 22px;
  right: 26px;
}

.login-panel {
  width: 100%;
  max-width: 352px;
}

.login-logo {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 26px;
}

.login-logo svg {
  width: 38px;
  height: 38px;
  flex: none;
}

.login-logo-zh {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.4px;
  line-height: 1.2;
}

.login-logo-en {
  font-size: 11px;
  color: var(--color-text-3);
  letter-spacing: 0.6px;
  margin-top: 3px;
  text-transform: uppercase;
}

.login-title {
  font-size: 23px;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin: 0 0 8px;
}

.login-sub {
  font-size: 13px;
  color: var(--color-text-3);
  margin: 0 0 26px;
  line-height: 1.6;
}

.login-error {
  margin-bottom: 14px;
}

.login-field {
  margin-bottom: 14px;
}

.login-field :deep(.arco-input) {
  border-radius: 8px;
  transition: border-color 0.18s, box-shadow 0.18s;
}

.login-field :deep(.arco-input:hover) {
  border-color: rgb(var(--primary-5));
}

.login-field-icon {
  width: 16px;
  height: 16px;
  display: block;
  color: var(--color-text-3);
}

.login-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2px 0 20px;
}

.login-row :deep(.arco-btn),
.login-split :deep(.arco-btn-primary) {
  border-radius: 8px;
}

.login-split :deep(.arco-btn-primary[type='submit']),
.login-form-panel .arco-btn-primary {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.login-foot {
  position: absolute;
  bottom: 24px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: var(--color-text-3);
  padding: 0 20px;
}

/* ---------- 平板竖屏以下：收起配图 ---------- */
@media (max-width: 920px) {
  .login-art-panel {
    display: none;
  }

  .login-form-panel {
    justify-content: flex-start;
    padding: 0 20px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .login-panel {
    max-width: 100%;
    margin-top: auto;
    margin-bottom: auto;
    padding: 34px 0;
  }

  .login-foot {
    position: static;
    margin-top: 26px;
    padding: 0;
  }

  .login-lang {
    top: 16px;
    right: 16px;
  }

  .login-title {
    font-size: 21px;
  }

  .login-logo {
    margin-bottom: 22px;
  }
}

/* ---------- 小屏收紧 ---------- */
@media (max-width: 420px) {
  .login-form-panel {
    padding: 0 16px;
  }

  .login-panel {
    padding: 24px 0;
  }

  .login-title {
    font-size: 20px;
  }

  .login-sub {
    margin-bottom: 20px;
  }
}

/* ---------- 横屏手机：允许滚动 ---------- */
@media (max-height: 520px) {
  .login-form-panel {
    justify-content: flex-start;
  }

  .login-panel {
    margin: 0 auto;
    padding: 20px 0;
  }
}
</style>
